import * as path from 'path';

import { Vector3 } from 'three';
import type * as zod from 'zod';

import type { Plane } from '@sb/geometry';
import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types';
import type {
  CameraIntrinsicsExtended,
  ExtrinsicsKinematics,
} from '@sb/integrations/types/cameraTypes';
import * as log from '@sb/log';
import type { ArmJointPositions } from '@sb/motion-planning';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { Space } from '@sb/routine-runner/types';
import type { LocateResult } from '@sb/routine-runner/vision/LocateTypes';
import { runLocate } from '@sb/routine-runner/vision/runLocate';
import { loadHostIdentity } from '@sb/service-interfaces/host/identity';
import { six } from '@sb/utilities';

import type { ImageValidationFailure } from '../Capture/captureUtils';
import {
  formatCaptureTimestamp,
  saveImageToStorage,
  saveMetadataToStorage,
} from '../Capture/captureUtils';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import { applyTextPromptOverride } from './applyTextPromptOverride';
import Arguments from './Arguments';
import {
  mapMatchesToFilters,
  mapSkillMethodToLocateMethod,
  skillTransformToCartesian,
} from './mapSkillToLocate';
import { resolveTextPromptOverride } from './resolveTextPromptOverride';
import Variables from './Variables';

const ns = log.namespace('runLocateSkillStep');

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

/**
 * We save a dummy set of joint angles for each position. Calculation of the joint angles
 * to use to reach the position should be deferred until a move arm step is played
 */
const NULL_JOINT_ANGLES: ArmJointPositions = six(0);

/**
 * Builds a runLocate `Plane` from three inline skill poses. Mirrors the inline
 * Locate step's `getPlaneFromSpaceItemID`, which uses position[0]=origin,
 * [1]=plusX, [2]=plusY.
 */
function planeFromPoses(
  origin: { x: number; y: number; z: number },
  plusX: { x: number; y: number; z: number },
  plusY: { x: number; y: number; z: number },
): Plane {
  return {
    origin: new Vector3(origin.x, origin.y, origin.z),
    plusX: new Vector3(plusX.x, plusX.y, plusX.z),
    plusY: new Vector3(plusY.x, plusY.y, plusY.z),
  };
}

export default class RunLocateSkillStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {
      latestResult: null,
      resultCount: 0,
      latestSaveFilepath: null,
      captureTimestamp: null,
      fullResults: null,
    };
  }

  /**
   * If `captureConfig` is set, saves the locate image to storage and sets
   * capture-related variables. Mirrors the inline Locate step.
   *
   * Returns an `ImageValidationFailure` when the image fails validation
   * (caller should pass it to `fail`), or `null` on success / when capture
   * is not applicable.
   */
  private async maybeCaptureImage(
    image: ArrayBuffer,
    skillId: string,
    locateResults?: LocateResult[],
  ): Promise<ImageValidationFailure | null> {
    const { captureConfig } = this.args;

    if (!captureConfig) {
      return null;
    }

    const formattedTimestamp = formatCaptureTimestamp(new Date());
    this.setVariable('captureTimestamp', formattedTimestamp);

    const remotePath = await this.routineFrame.evaluateExpression(
      captureConfig.saveFilepath,
    );

    if (typeof remotePath !== 'string') {
      throw new Error(
        `Filepath expression must evaluate to a string, received ${typeof remotePath}`,
      );
    }

    if (!remotePath) {
      throw new Error(
        'Filepath expression must evaluate to a non-empty string',
      );
    }

    if (!remotePath.endsWith('.png')) {
      throw new Error(`Filepath must end with .png (Filepath: ${remotePath})`);
    }

    const storageEquipment = this.routineContext.equipment.getDeviceById(
      captureConfig.saveEquipmentId,
    );

    if (!storageEquipment) {
      throw new Error(
        `Storage equipment not found: ${captureConfig.saveEquipmentId}`,
      );
    }

    const validationFailure = await saveImageToStorage({
      image,
      remotePath,
      storageEquipment,
    });

    if (validationFailure) {
      return validationFailure;
    }

    this.setVariable('latestSaveFilepath', remotePath);

    await this.maybeSaveMetadata(
      remotePath,
      storageEquipment,
      skillId,
      locateResults,
    );

    log.info(ns`runLocateSkill.capture`, 'RunLocateSkill capture completed', {
      remotePath,
    });

    return null;
  }

  /**
   * If `saveMetadata` is enabled, builds a metadata object for the captured
   * image and uploads it alongside the image as a JSON sidecar file.
   */
  private async maybeSaveMetadata(
    remotePath: string,
    storageEquipment: { kind: string; executeCommand: (...args: any[]) => any },
    skillId: string,
    locateResults?: LocateResult[],
  ): Promise<void> {
    if (!this.args.captureConfig?.saveMetadata) {
      return;
    }

    const identity = await loadHostIdentity();

    const { captureConfig: _captureConfig, ...stepParameters } = this.args;

    await saveMetadataToStorage({
      metadata: {
        imageFilename: path.basename(remotePath),
        botId: this.routineContext.robotId,
        botAlias: identity?.alias,
        timestamp: new Date().toISOString(),
        stepKind: 'RunLocateSkill',
        skillId,
        locateResults,
        stepParameters,
      },
      remotePath,
      storageEquipment,
    });
  }

  /**
   * Resolve a Space plane item by id, validating it really is a plane. Shared
   * by {@link resolvePlane} and the accuracy-calibration lookup so the item is
   * fetched from the routine context only once per play.
   */
  private resolvePlaneItem(planeSpaceItemId: string): Space.Plane {
    const item = this.routineFrame.getSpaceItem(planeSpaceItemId);

    if (item.kind !== 'plane') {
      throw new Error(
        `RunLocateSkill: Space item ${planeSpaceItemId} is not a plane`,
      );
    }

    return item as Space.Plane;
  }

  /**
   * Build the runLocate `Plane` from the skill's projection config: an inline
   * `plane` ({ origin, xAxis, yAxis }) when present, else the pre-resolved
   * `planeItem`'s first three positions, else undefined.
   */
  private resolvePlane(
    projection: NonNullable<
      Awaited<ReturnType<typeof this.routineContext.getLocateSkillById>>
    >['shared']['projection'],
    planeItem: Space.Plane | undefined,
  ): Plane | undefined {
    if (projection.plane) {
      return planeFromPoses(
        projection.plane.origin,
        projection.plane.xAxis,
        projection.plane.yAxis,
      );
    }

    if (planeItem) {
      return planeFromPoses(
        planeItem.positions[0].pose,
        planeItem.positions[1].pose,
        planeItem.positions[2].pose,
      );
    }

    return undefined;
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    try {
      log.info(ns`runLocateSkill.play`, 'Playing RunLocateSkill step', {
        skillId: this.args.skillId,
      });

      // Resolve the saved Locate skill at runtime.
      const skill = await this.routineContext.getLocateSkillById(
        this.args.skillId,
      );

      const { shared } = skill;

      // Skills are camera-bot-cpp only — resolve the camera by ID.
      const cameraEquipment = this.routineContext.equipment.getDeviceById(
        shared.cameraId,
      ) as Camera | undefined;

      if (!cameraEquipment) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: `RunLocateSkill: camera equipment with id ${shared.cameraId} not found`,
        });
      }

      const cameraConfig = cameraEquipment.getConfig() as CameraConfiguration;
      const { extrinsicsId, intrinsicsId } = cameraConfig;

      if (extrinsicsId == null || intrinsicsId == null) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: `RunLocateSkill: camera ${shared.cameraId} has no extrinsics or intrinsics set.`,
        });
      }

      const extrinsics: ExtrinsicsKinematics | undefined =
        cameraEquipment.getExtrinsics(true);

      // Guard the index: a stale `intrinsicsId` (entry no longer present)
      // would otherwise throw a `TypeError` that the outer try/catch turns
      // into a generic `StepPlayFailure`. Optional-chaining lets it fall
      // through to the explicit `CameraFailure` below with a clear message.
      const intrinsics: CameraIntrinsicsExtended | undefined =
        cameraConfig.intrinsicsData[intrinsicsId]?.intrinsics ?? undefined;

      if (extrinsics == null) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: `RunLocateSkill: camera ${shared.cameraId} has no extrinsics set.`,
        });
      }

      if (intrinsics == null) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: `RunLocateSkill: camera ${shared.cameraId} has no intrinsics set.`,
        });
      }

      // Derive the locate method and result filters from the skill config.
      // Apply the step-level text-prompt override (text_prompt only) when
      // present. DinoFast no longer carries a rotation toggle — the SAM2
      // contour is the source of truth for the final shape, so the detector
      // always fits oriented bboxes.
      // Resolve the step-level text-prompt override (text_prompt only). A
      // literal string is used as-is; an Expression (e.g. a routine variable)
      // is evaluated now. Skipped for other methods so we never evaluate an
      // expression that applyTextPromptOverride would discard.
      const resolvedTextPrompt =
        skill.method === 'text_prompt'
          ? await resolveTextPromptOverride(
              this.args.textPromptOverride,
              (expr) => this.routineFrame.evaluateExpression(expr),
            )
          : undefined;

      const effectiveMethodConfig = applyTextPromptOverride(
        skill.method,
        skill.methodConfig,
        resolvedTextPrompt,
      );

      const method = mapSkillMethodToLocateMethod(
        skill.method,
        effectiveMethodConfig,
        shared.matches,
      );

      const { resultsLimit, resultOrdering } = mapMatchesToFilters(
        shared.matches,
      );

      // Resolve the Space plane item once (when a Space plane is used) and
      // reuse it for both the plane geometry (in the plane-projection branch
      // below) and the accuracy-calibration lookup, instead of fetching it
      // from the routine context twice.
      const planeItem =
        shared.projection.planeSpaceItemId != null
          ? this.resolvePlaneItem(shared.projection.planeSpaceItemId)
          : undefined;

      const transform = skillTransformToCartesian(shared.projection.transform);

      // Branch on the skill's projection method:
      // - 'plane' (default): project the 2D detections onto the taught plane.
      // - 'detection3d': estimate a 6D pose per detection from a depth frame
      //   via the Detection3D backend. No plane is resolved in this mode.
      const isDetection3D = shared.projection.method === 'detection3d';

      let plane: Plane | undefined;
      let depthImage: ArrayBuffer | undefined;
      let projectionMode: 'Detection3D' | undefined;

      const detection3dConfig = shared.projection.detection3d;

      if (isDetection3D) {
        if (detection3dConfig == null) {
          return fail({
            failure: {
              kind: FailureKind.StepPlayFailure,
              stepKind: this.getStepKind(),
            },
            failureReason: `RunLocateSkill: projection method is 'detection3d' but no detection3d config is set`,
          });
        }

        // 3D detection needs a depth frame from a depth-capable camera.
        try {
          depthImage = await cameraEquipment.getDepthFrame();
        } catch (depthError) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `RunLocateSkill: camera ${shared.cameraId} failed to return a depth frame: ${(depthError as Error).message}; 3D detection requires a depth-capable camera`,
            error: depthError as Error,
          });
        }

        if (depthImage == null || depthImage.byteLength === 0) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `RunLocateSkill: camera ${shared.cameraId} returned an empty depth frame; 3D detection requires a depth-capable camera`,
          });
        }

        projectionMode = 'Detection3D';
      } else {
        // Plane projection: resolve the taught plane (inline or Space item).
        plane = this.resolvePlane(shared.projection, planeItem);
      }

      // Accuracy calibration: look up the calibration matching this camera's
      // active extrinsics from the resolved plane (when a Space plane is used).
      // Detection3D has no Space plane, so this stays null.
      let accuracyCalibration: Space.AccuracyCalibrationEntry[] | null = null;

      if (planeItem) {
        // `cameraAccuracyCalibrations` is optional on the Space.Plane schema —
        // older planes or fresh user-taught planes may not have any entries yet.
        const calibration = Object.values(
          planeItem.cameraAccuracyCalibrations ?? {},
        ).find(
          (entry) =>
            entry.equipmentId === shared.cameraId &&
            entry.extrinsicsId === extrinsicsId,
        );

        accuracyCalibration = calibration?.calibrationEntries ?? null;
      }

      const { results: runLocateResults, image } = await runLocate({
        camera: {
          equipment: cameraEquipment,
          // Skills are camera-bot-cpp only; the legacy CameraIntegration is
          // not used for these cameras.
          integration: undefined,
        },
        regionOfInterest: shared.regionOfInterest ?? {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        },
        method,
        plane,
        // Config stores mm (matching the transform offsets); runLocate takes
        // meters. Nullish on skills saved before the field existed.
        objectHeight: (shared.projection.objectHeight ?? 0) * 0.001,
        resultsLimit,
        transform,
        accuracyCalibration,
        vision: this.routineContext.vision,
        getState: () => this.routineContext.getRoutineRunnerState(),
        equipment: this.routineContext.equipment,
        intrinsics,
        extrinsics,
        planeDetectionMode: 'Manual',
        // The projection transform's grasp frame (object / plane / world).
        // `graspFrame` is a frame, not part of the offset pose, so
        // it travels alongside `transform`. Defaults to 'object' for older saved
        // skills.
        graspFrame: shared.projection.transform?.graspFrame ?? 'object',
        resultOrdering,
        projectionMode,
        detection3dConfig: detection3dConfig ?? undefined,
        depthImage,
      });

      const positions: Space.Position[] = runLocateResults.map((result) => {
        return {
          pose: result.pose,
          jointAngles: NULL_JOINT_ANGLES,
          tcpOption: 'ee-any',
        };
      });

      this.setVariable('latestResult', positions);
      this.setVariable('resultCount', positions.length);
      this.setVariable('fullResults', runLocateResults);

      // Write results to the step's own position list (keyed by step id).
      this.routineFrame.setPositionListEntries(this.id, positions);

      // Best-effort execution-history persist (rolling window on the skill
      // record). Never fails the step — visualization is non-critical. Runs
      // before the capture step so history survives a capture failure.
      try {
        await this.routineContext.updateLocateSkillExecutions({
          skillId: this.args.skillId,
          image,
          timestamp: new Date().toISOString(),
          imageWidth: intrinsics.width,
          imageHeight: intrinsics.height,
          matches: runLocateResults.map((result) => ({
            // `blob` is the lean Detection2D box; pick its fields explicitly
            // to match LeanDetection — see libs/feathers-types/skill.ts for
            // the duplication rationale.
            detection: {
              x: result.blob.x,
              y: result.blob.y,
              width: result.blob.width,
              height: result.blob.height,
              rotation: result.blob.rotation,
              score: result.blob.score,
              className: result.blob.className,
              classId: result.blob.classId,
            },
            // SAM2 outlines live on `segment` (a Blob2D, null when
            // segmentation was off). Persist only its contour — the segment
            // also carries heavyweight extras (mask RLE, intensity stats)
            // that must stay out of the rolling history record.
            contour: result.segment?.contour ?? [],
          })),
        });
      } catch (error) {
        log.warn(
          ns`runLocateSkill.history`,
          'Failed to persist execution history',
          { skillId: this.args.skillId, error },
        );
      }

      const captureFailure = await this.maybeCaptureImage(
        image,
        this.args.skillId,
        runLocateResults,
      );

      if (captureFailure) {
        return fail(captureFailure);
      }
    } catch (error) {
      log.error(ns`runLocateSkill.play`, 'RunLocateSkill failed', error);

      if ((error as any).failureKind === FailureKind.CameraFailure) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: (error as Error).message,
          error: error as Error,
        });
      }

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          // Resolved dynamically from the registry so this stays type-safe
          // regardless of when the 'RunLocateSkill' kind is registered.
          stepKind: this.getStepKind(),
        },
        failureReason: `${(error as Error).message}`,
        error: error as Error,
      });
    }
  }
}
